<template>
	<div class="ledger-page pb-5">
		<header-page
			:title="$route.meta.title"
		></header-page>

		<v-layout wrap px-5>
			<!-- ============ BARRA DE BUSQUEDA Y PERIODO ============ -->
			<v-flex xs12>
				<div class="ledger-toolbar">
					<v-layout row wrap align-end>
						<v-flex xs12 md5 class="toolbar-item">
							<accounting-searcher
								:account="account.account"
								:account-name="account.name"
								@open-searcher="openSearcher"
								@update:account="handleAccountUpdate"
								@update:accountName="handleAccountNameUpdate"
							/>
						</v-flex>
						<v-flex xs12 sm4 md3 lg2 class="toolbar-item">
							<BaseSelect
								item-text="name"
								item-value="id"
								:height="32"
								label="Período"
								:items="typesDate"
								@input="getDataDate($event)"
							/>
						</v-flex>
						<v-flex v-if="flagDate" xs6 sm4 md2 lg2 class="toolbar-item">
							<span class="field-label">Fecha desde</span>
							<date-component
								round
								:date="model.startDate"
								icon="/static/img/calendar.svg"
								@on-input="getDate({ model: 'startDate', value: $event })"
							></date-component>
						</v-flex>
						<v-flex v-if="flagDate" xs6 sm4 md2 lg2 class="toolbar-item">
							<span class="field-label">Fecha hasta</span>
							<date-component
								round
								:date="model.endDate"
								icon="/static/img/calendar.svg"
								@on-input="getDate({ model: 'endDate', value: $event })"
							></date-component>
						</v-flex>
					</v-layout>
				</div>
			</v-flex>

			<!-- ============ TOTALES DE LA CUENTA ============ -->
			<v-flex xs12>
				<v-layout row wrap justify-end class="ledger-totals">
					<v-flex class="total-chip total-chip--debit">
						<span class="total-chip__label">Debe</span>
						<span class="total-chip__value">{{ debit | formatAmount }}</span>
					</v-flex>
					<v-flex class="total-chip total-chip--credit">
						<span class="total-chip__label">Haber</span>
						<span class="total-chip__value">{{ credit | formatAmount }}</span>
					</v-flex>
					<v-flex class="total-chip total-chip--balance">
						<span class="total-chip__label">Saldo</span>
						<span class="total-chip__value">{{ amount | formatAmount }}</span>
					</v-flex>
				</v-layout>
			</v-flex>

			<!-- ============ MOVIMIENTOS ============ -->
			<v-flex xs12>
				<div class="ledger-card">
					<div class="ledger-card__head">
						<div>
							<p class="ledger-card__title mb-0">Movimientos de la cuenta</p>
							<p class="ledger-card__sub mb-0">
								{{ account.name || 'Seleccione una cuenta contable' }}
								<span v-if="numberSeats"> · {{ numberSeats }} asientos</span>
							</p>
						</div>
					</div>
					<div class="ledger-card__body">
						<ledger-list
							:current-page="currentPage"
							:last-page="lastPage"
							:items-quantity="rows.length"
							:items-total-quantity="itemsTotalQuantity"
							:rows="rows"
							:number-seats="numberSeats"
							@on-search="onSearchData"
							@on-page="changePageDetails"
							@row-on-click="rowIsSelected"
							@on-click="rowIsSelected"
							@up-selected="upSelected($event, 'rows', 'rowIsSelected')"
							@down-selected="downSelected($event, 'rows', 'rowIsSelected')"
						></ledger-list>
					</div>
				</div>
			</v-flex>

			<!-- ============ DETALLE DEL ASIENTO ============ -->
			<v-flex xs12>
				<div class="ledger-card">
					<div class="ledger-card__head">
						<div>
							<p class="ledger-card__title mb-0">Detalle del asiento</p>
							<p class="ledger-card__sub mb-0">Datos del movimiento seleccionado</p>
						</div>
					</div>

					<general-filter class="layout ledger-detail">
						<v-flex class="detail-item">
							<span class="detail-item__label">Fecha</span>
							<span class="detail-item__value">
								{{ rowSelected.accountingSeat.date | exactDate }}
							</span>
						</v-flex>
						<v-flex class="detail-item">
							<span class="detail-item__label">Asiento</span>
							<span class="detail-item__value">
								{{ rowSelected.accountingSeat.code || '—' }}
							</span>
						</v-flex>
						<v-flex class="detail-item">
							<span class="detail-item__label">Referencia</span>
							<span class="detail-item__value">{{ detailSelected.reference || '—' }}</span>
						</v-flex>
						<v-flex class="detail-item">
							<span class="detail-item__label">M&oacute;dulo</span>
							<span class="detail-item__value">
								{{ detailSelected && detailSelected.module ? detailSelected.module.code : '—' }}
							</span>
						</v-flex>
						<v-flex class="detail-item">
							<span class="detail-item__label">Descripci&oacute;n</span>
							<span class="detail-item__value" :title="rowSelected.accountingSeat.gloss">
								{{ rowSelected.accountingSeat.gloss || '—' }}
							</span>
						</v-flex>
						<v-flex class="detail-item">
							<span class="detail-item__label">Concepto</span>
							<span class="detail-item__value" :title="rowSelected.concept">
								{{ rowSelected.concept || '—' }}
							</span>
						</v-flex>
					</general-filter>

					<div class="ledger-card__body">
						<ledger-detail-list
							:rows="rowsDetails"
							@row-on-click="rowIsSelectedDetail"
							@up-selected="upSelected($event, 'rowsDetails', 'rowIsSelectedDetail')"
							@down-selected="downSelected($event, 'rowsDetails', 'rowIsSelectedDetail')"
						></ledger-detail-list>
					</div>

					<div class="ledger-card__foot">
						<journal-detail-balance
							:balance="balance"
							:credit="rowSelected.accountingSeat.totalHaber"
							:debit="rowSelected.accountingSeat.totalDebe"
						/>
					</div>
				</div>
			</v-flex>
		</v-layout>

		<action-sidebar
			:title="action.title"
			:value="action.value"
			:width="action.width"
			@close-modal="closeModal"
		>
			<slide-searcher
				v-if="isActionCode('searcher')"
				:columns="columns"
				:current-page="currentPageSearcher"
				:last-page="lastPageSearcher"
				:rows="records"
				:account-name="account.name"
				:subsidiaries="subsidiaries"
				:subsidiary-id="subsidiaryId"
				text-btn="Seleccionar"
				@add-record="addRecord"
				@on-close="closeModal"
				@on-page="changePage"
				@on-search="onSearchRecords"
				@search-subsidiary="searchSubsidiary"
				@set-id="setId"
			/>
		</action-sidebar>
	</div>
</template>

<script>
	import { mapGetters } from 'vuex';
	import accountingSearcher from '@/components/shared/searcher/accounting-searcher';
	import actionSidebar from '@/components/shared/action-sidebar';
	import helper from '@/shared/helper';
	import journalDetailBalance from '@/components/journal/journal-detail-balance';
	import ledgerDetailList from '@/components/ledger/ledger-detail-list';
	import ledgerList from '@/components/ledger/ledger-list';
	import slideSearcher from '@/components/shared/searcher/slide-searcher';
	import baseInput from '@/components/shared/inputs/base-input';
	import {
		endOfMonth,
		format,
		startOfMonth,
		subDays,
	} from 'date-fns';
	import dateComponent from '@/components/autocomplete/date-component';

	const { debounce } = helper;
	const FORMAT_DATE = 'YYYY-MM-DD';

	function created() {
		if (this.currentSubsidiary) {
			this.newSubsidiaryId = this.currentSubsidiary.id;
		}
		// startOfMonth evita el desfase de getMonth(), que es base 0
		this.model.startDate = format(startOfMonth(new Date()), FORMAT_DATE);
		this.model.endDate = this.$today;
		this.loadRecords();
		// this.loadAccounting();
	}
	function getDate({ value, model }) {
		if (model === 'startDate') {
			this.model[model] = format(value, FORMAT_DATE);
			this.model.endDate = format(endOfMonth(value), FORMAT_DATE);
		} else if (model === 'endDate') {
			this.model[model] = format(value, FORMAT_DATE);
		}
		this.loadData();
	}

	function addRecord(record) {
		this.account = { ...record };
		localStorage.setItem('quipu::Acount', this.account.id);
		this.action.value = false;
		this.action.title = '';
		this.action.code = '';
		this.rows = [];
		this.rowsDetails = [];
		this.rowSelected = { accountingSeat: { totalDebe: 0, totalHaber: 0 } };
		this.detailSelected = {};
		this.loadData();
	}

	function changePage(page) {
		this.loadRecords(page);
	}
	function changePageDetails(page) {
		this.loadData(page);
	}

	function closeModal() {
		this.action.value = false;
		this.action.code = '';
		delete this.accountingParams.search;
	}

	function downSelected(row, type, typeSelected) {
		const index = this[type].findIndex(r => r.id === row.id);
		if (index > -1 && index < (this[type].length - 1)) {
			this[typeSelected](this[type][index + 1]);
		}
	}

	function isActionCode(code) {
		return this.action.code === code;
	}

	async function loadData(page = 1) {
		this.currentPage = page;
		this.params.page = this.currentPage;
		const startDate = this.model.startDate;
		const endDate = this.model.endDate;
		const params = this.params;

		try {
			const { data: response, headers } = await this.$http.get(
				`accounting-seat-details/${this.account.id}/${this.currentSubsidiary.id}?startDate=${startDate}&endDate=${endDate}`, { params });
			this.debit = Number(headers.totaldebe);
			this.credit = Number(headers.totalhaber);
			this.amount = Number(headers.totalbalance);
			this.numberSeats = Number(headers['x-quantity']);
			this.itemsTotalQuantity = Number(headers['x-quantity']);
			this.rows = response.map((row, index) => {
				const newRow = { ...row };
				newRow.isSelected = index === 0;
				return newRow;
			});
			this.rowsDetails = [];
			this.detailSelected = {};
			this.rowSelected = { accountingSeat: { totalDebe: 0, totalHaber: 0 } };
			this.rowIsSelected(this.rows.find(row => row.isSelected));
			this.lastPage = Number(headers['x-last-page']);
		} catch (err) {
			if (err.status === 500) {
				this.showGenericError();
			}
		}
	}

	async function loadRecords(page = 1) {
		this.currentPageSearcher = page;
		this.paramsRecords.page = this.currentPageSearcher;
		const subId = JSON.parse(localStorage.getItem('cs'));
		const params = { ...this.paramsRecords };
		// params.planAccountId = this.planAccountId ? this.planAccountId : 61;
		try {
			const { data: records, headers } = await this.$http.get(
				// `accounting-plan/${subId.id}/subsidiary?flag=false`, { params });
				`accounting-plan/${subId.id}/subsidiary?status=1`, { params });
			this.lastPageSearcher = Number(headers['x-last-page']);
			this.records = [...records];
			this.rowSelected();
		} catch (err) {
			if (err.status === 500) {
				this.showGenericError();
			}
		}
	}

	function onSearchData(search) {
		this.params.search = search.trim() ? search.trim() : null;
		this.loadData();
	}

	function onSearchDataDetail(search) {
		this.paramsDetail.search = search.trim() ? search.trim() : null;
	}

	function onSearchRecords(search) {
		this.paramsRecords.search = search.trim() ? search.trim() : null;
		this.loadRecords();
	}

	function openSearcher() {
		this.paramsRecords.search = null;
		if (this.newSubsidiaryId) {
			this.loadRecords();
		}
		this.action.title = 'Buscar Cuenta Contable';
		this.action.code = 'searcher';
		this.action.width = 550;
		this.action.value = true;
	}

	function rowIsSelected(row) {
		this.rows = this.rows.map((r) => {
			const newRow = { ...r };
			newRow.isSelected = row.id === r.id;
			return newRow;
		});
		this.rowSelected = row.id ? { ...row } :
			{ accountingSeat: {}, totalDebe: 0, totalHaber: 0 };
		this.rowsDetails = this.rowSelected.id ?
			this.rowSelected.accountingSeat.details.map((rowDetail, index) => {
				const newRowDetail = { ...rowDetail };
				newRowDetail.module = this.rowSelected.accountingSeat &&
					this.rowSelected.accountingSeat.typeRegister ?
					this.rowSelected.accountingSeat.typeRegister : null;
				newRowDetail.isSelected = index === 0;
				return newRowDetail;
			}) : [];
		this.rowIsSelectedDetail(this.rowsDetails[0]);
		this.onclick(row);
	}

	function onclick(row) {
		this.rows = this.rows.map((r) => {
			const newRow = { ...r };
			newRow.isSelected = row.id === r.id;
			return newRow;
		});
		this.rowSelected = row.id ? { ...row } :
			{ accountingSeat: {}, totalDebe: 0, totalHaber: 0 };
		this.rowsDetails = this.rowSelected.id ?
			this.rowSelected.accountingSeat.details.map((rowDetail, index) => {
				const newRowDetail = { ...rowDetail };
				newRowDetail.isSelected = index === 0;
				newRowDetail.module = this.rowSelected.accountingSeat &&
					this.rowSelected.accountingSeat.typeRegister ?
					this.rowSelected.accountingSeat.typeRegister : null;
				newRowDetail.isSelected = index === 0;
				return newRowDetail;
			}) : [];
		this.rowIsSelectedDetail(this.rowsDetails[0]);
	}

	function rowIsSelectedDetail(row) {
		this.rowsDetails = this.rowsDetails.map((detail) => {
			const newDetail = { ...detail };
			newDetail.isSelected = row.id === detail.id;
			return newDetail;
		});
		this.detailSelected = row.id ? { ...row } : {};
	}

	function rowSelectedWatcher(value) {
		const debt = Number(value.accountingSeat.totalDebe) || null;
		const credit = Number(value.accountingSeat.totalHaber) || null;
		this.balance = debt - credit;
	}

	async function searchSubsidiary(value, detail) {
		const params = detail ? null : { search: value };
		const url = detail ? `subsidiary/${value}/detail` : 'subsidiary';
		try {
			const { data: response } = await this.$http.get(url, { params });
			this.subsidiaries = detail ? [{ ...response }] : [...response];
			if (detail) {
				this.setId({ id: this.subsidiaries[0].id });
			}
		} catch (err) {
			if (err.status === 500) {
				this.showGenericError();
			} else if (err.status === 400) {
				this.showGenericError(err.data.message);
			}
		}
	}
	async function subsidiaryChange(newSubsidiary) {
		this.loadRecords(1, newSubsidiary);
	}
	function setId() {
		this.loadRecords();
	}

	function getDataDate(val) {
		if (val === 1) {
			this.model.startDate = this.$today;
			this.model.endDate = this.$today;
			this.flagDate = false;
		} else if (val === 2) {
			this.model.startDate = format(subDays(this.$today, 1), FORMAT_DATE);
			this.model.endDate = format(subDays(this.$today, 1), FORMAT_DATE);
			this.flagDate = false;
		} else if (val === 3) {
			this.model.startDate = format(subDays(this.$today, 7), FORMAT_DATE);
			this.model.endDate = this.$today;
			this.flagDate = false;
		} else if (val === 4) {
			const today = new Date(this.$today);
			const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
			this.model.startDate = firstDayOfMonth.toISOString().split('T')[0];
			this.model.endDate = this.$today;
			this.flagDate = false;
		} else if (val === 5) {
			const today = new Date(this.$today);
			// Set the startDate to the first day of the current year
			const firstDayOfYear = new Date(today.getFullYear(), 0, 1);
			this.model.startDate = firstDayOfYear.toISOString().split('T')[0];
			this.model.endDate = this.$today;
			this.flagDate = false;
		} else if (val === 6) {
			this.model.startDate = format(subDays(this.$today, 7), FORMAT_DATE);
			this.model.endDate = this.$today;
			this.flagDate = true;
		}
		this.loadData();
	}

	function upSelected(row, type, typeSelected) {
		const index = this[type].findIndex(r => r.id === row.id);
		if (index > 0) {
			this[typeSelected](this[type][index - 1]);
		}
	}

	// async function loadAccounting() {
	// 	try {
	// 		const { data: response } = await this.$http.get('com-accounting-plan');
	// 		this.accountingPlan = response;
	// 	} catch (error) {
	// 		this.showGenericError();
	// 	}
	// }

	// function getAccountingPlan(accountId) {
	// 	this.planAccountId = accountId;
	// 	this.loadRecords();
	// }

	function data() {
		return {
			account: {
				name: null,
				account: null,
				id: null,
			},
			model: {
				endDate: 0,
				startDate: 0,
			},
			accountingParams: {
				limit: 20,
				page: 1,
				status: 1,
			},
			action: {
				code: '',
				title: 'Buscar Cuenta Contable',
				value: false,
				width: 500,
			},
			balance: 0,
			columns: [
				{
					id: 1,
					name: 'Nombre',
					value: 'name',
					checked: true,
					visible: true,
				},
				{
					id: 2,
					name: 'Cuenta',
					value: 'account',
					checked: true,
					visible: true,
				},
				{
					id: 3,
					name: 'Acción',
					checked: true,
					visible: true,
					flagVisible: true,
				},
			],
			currentPage: 1,
			currentPageSearcher: 1,
			detailsSelected: [],
			detailSelected: {
				accountingAccount: {},
				module: {},
			},
			lastPage: 0,
			flagDate: false,
			lastPageSearcher: 0,
			params: {
				limit: 10,
				page: 1,
			},
			paramsRecords: {
				limit: 30,
				page: 1,
			},
			records: [],
			typesDate: [
				{ id: 1, name: 'Hoy' },
				{ id: 2, name: 'Ayer' },
				{ id: 3, name: 'Ultima Semana' },
				{ id: 4, name: 'Mes Actual' },
				{ id: 5, name: 'Año Actual' },
				{ id: 6, name: 'Rangos de Fechas' },
			],
			rows: [],
			rowsDetails: [],
			rowSelected: {
				accountingSeat: {
					totalDebe: 0,
					totalHaber: 0,
				},
			},
			subsidiaries: [],
			debit: 0,
			credit: 0,
			amount: 0,
			newSubsidiaryId: null,
			total: null,
			numberSeats: null,
			itemsTotalQuantity: null,
			// accountingPlan: [],
			// planAccountId: 61,
		};
	}

	export default {
		name: 'page-ledger',
		computed: {
			...mapGetters('subsidiary', [
				'currentSubsidiary',
			]),
		},
		components: {
			accountingSearcher,
			actionSidebar,
			journalDetailBalance,
			ledgerDetailList,
			ledgerList,
			slideSearcher,
			baseInput,
			dateComponent,
			BaseSelect: () => import('@/components/shared/inputs/base-select'),
		},
		created,
		data,
		methods: {
			addRecord,
			changePage,
			onclick,
			closeModal,
			downSelected,
			isActionCode,
			loadData,
			loadRecords,
			onSearchData,
			onSearchDataDetail,
			onSearchRecords,
			openSearcher,
			getDataDate,
			rowIsSelected,
			rowIsSelectedDetail,
			searchSubsidiary: debounce(searchSubsidiary),
			setId,
			upSelected,
			getDate,
			changePageDetails,
			handleAccountUpdate(value) {
				this.account.account = value;
			},
			handleAccountNameUpdate(value) {
				this.account.name = value;
			},
			// loadAccounting,
			// getAccountingPlan,
		},
		props: {
			subsidiaryId: [Number, String],
		},
		watch: {
			rowSelected: {
				deep: true,
				handler: rowSelectedWatcher,
			},
			currentSubsidiary: subsidiaryChange,
		},
	};
</script>

<style lang="scss" scoped>
	$ledger-line: #e3e9ec;
	$ledger-ink-2: #5b6b76;
	$ledger-plane: #f4f7f8;
	$ledger-teal: #02979b;
	$ledger-blue: #77a1d1;

	.ledger-page {
		background-color: $ledger-plane;
	}

	/* ---------------- barra de filtros ---------------- */
	.ledger-toolbar {
		background-color: #ffffff;
		border: 1px solid $ledger-line;
		border-radius: 12px;
		margin-top: 12px;
		padding: 12px 16px;
		width: 100%;
	}

	.toolbar-item {
		padding-right: 12px;

		&:last-child {
			padding-right: 0;
		}
	}

	.field-label {
		color: $ledger-ink-2;
		display: block;
		font-family: 'AvenirRegular';
		font-size: 9.5px;
		font-weight: 700;
		letter-spacing: 0.9px;
		margin-bottom: 4px;
		text-transform: uppercase;
	}

	/* ---------------- totales de la cuenta ---------------- */
	.ledger-totals {
		margin: 14px 0 4px;
	}

	.total-chip {
		align-items: baseline;
		border-radius: 10px;
		color: #ffffff;
		display: flex;
		flex: 0 0 auto;
		justify-content: space-between;
		margin-left: 10px;
		min-width: 180px;
		padding: 8px 14px;

		@media (max-width: 768px) {
			margin-bottom: 6px;
			margin-left: 0;
			min-width: 100%;
		}
	}

	.total-chip__label {
		font-family: 'AvenirRegular';
		font-size: 10px;
		font-weight: 700;
		letter-spacing: 1px;
		opacity: 0.85;
		text-transform: uppercase;
	}

	.total-chip__value {
		font-family: 'AvenirRegular';
		font-size: 16px;
		font-weight: 700;
	}

	.total-chip--debit {
		background-color: $ledger-teal;
	}

	.total-chip--credit {
		background-color: color(secondary);
	}

	.total-chip--balance {
		background-color: $ledger-blue;
	}

	/* ---------------- tarjetas ---------------- */
	.ledger-card {
		background-color: #ffffff;
		border: 1px solid $ledger-line;
		border-radius: 12px;
		margin-top: 14px;
		overflow: hidden;
		width: 100%;
	}

	.ledger-card__head {
		align-items: center;
		border-bottom: 1px solid $ledger-line;
		display: flex;
		justify-content: space-between;
		padding: 10px 16px;
	}

	.ledger-card__title {
		font-family: 'AvenirRegular';
		font-size: 13px;
		font-weight: 700;
	}

	.ledger-card__sub {
		color: $ledger-ink-2;
		font-size: 11px;
	}

	.ledger-card__body {
		padding: 0 12px 10px;
	}

	.ledger-card__foot {
		background-color: $ledger-plane;
		border-top: 1px solid $ledger-line;
		padding: 8px 12px;
	}

	/* ---------------- detalle del asiento ---------------- */
	.ledger-detail {
		background-color: #ffffff !important;
		border-bottom: 1px solid $ledger-line;
		flex-wrap: wrap;
		margin: 0;
		padding: 0;
	}

	.detail-item {
		border-right: 1px solid $ledger-line;
		min-width: 0;
		padding: 10px 14px;

		&:last-child {
			border-right: none;
		}

		@media (max-width: 768px) {
			border-bottom: 1px solid $ledger-line;
			border-right: none;
			flex: 0 0 50%;
		}
	}

	.detail-item__label {
		color: $ledger-ink-2;
		display: block;
		font-family: 'AvenirRegular';
		font-size: 9.5px;
		font-weight: 700;
		letter-spacing: 0.9px;
		margin-bottom: 3px;
		text-transform: uppercase;
	}

	.detail-item__value {
		display: block;
		font-size: 13px;
		font-weight: 600;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
</style>


// WEBPACK FOOTER //
// src/pages/journal/page-ledger.vue